'use strict';

import * as Blockly from 'blockly/core';

import {buildSoundEffectOptions} from './soundfx';
import {useSongsStorage} from '../hooks/project';
import {MUSIC_ICON, STOP_ICON} from './icon';

const MUSIC_COLOR = 'rgb(255, 87, 34)';

// A pattern's length is adjustable (see clampPatternSteps below), but
// defaults to 16 steps (one bar at 2 steps/beat) and 140 BPM below - the same
// defaults FL Studio uses for a new project's step sequencer/tempo.
export const DEFAULT_PATTERN_STEPS = 16;

// Free-entry pattern length range (see MusicEditor.vue's  Length (steps)
// field) - 1 as the shortest possible pattern, 64 as the longest, matching
// LENGTH_UNITS_PER_STEP's  headroom and the piano roll's fixed-width grid
// (see maxPatternSteps in MusicEditor.vue).
export const MIN_PATTERN_STEPS = 1;
export const MAX_PATTERN_STEPS = 64;

// Applies to every Length (steps) field (see MusicEditor.vue's
// handleStepCountChange, and the load-time clamp below for values from an
// older save/an imported file whose stepCount predates this range or came
// from the old fixed dropdown). Anything above MAX_PATTERN_STEPS rounds down
// to it rather than being rejected, matching the field's  tooltip.
export const clampPatternSteps = (value) => {
  const steps = Math.round(Number(value));
  return Number.isFinite(steps) ? Math.min(MAX_PATTERN_STEPS, Math.max(MIN_PATTERN_STEPS, steps)) : DEFAULT_PATTERN_STEPS;
};

// How many equal slices a single piano-roll step can be divided into for
// note duration (set via the dropdown under the Music tab's  header) -
// e.g. 4 lets a note be as short as a quarter of a step. Note events store
// their length in fixed, fine-grained units (see LENGTH_UNITS_PER_STEP)
// regardless of this setting, so changing it later only changes the
// snapping granularity for NEW edits, never reinterprets already-placed
// notes' lengths.
export const DURATION_SUBDIVISION_OPTIONS = [1, 2, 4, 8, 16];
export const DEFAULT_SUBDIVISION = 1;

// A note event's "length" field is stored in units of 1/32 of a step -
// fine enough to exactly represent every selectable subdivision above
// (32 is the finest, so 1 of its slices = 1 unit) without ever needing
// fractional unit counts.
export const LENGTH_UNITS_PER_STEP = 32;

const emptyTrack = (id, soundEffectId, channel = 0) => ({
  id,
  soundEffectId,
  channel,
  // Sparse list of note events, not one slot per step - {step, midi, audf,
  // length} - so a note can be held across several steps, or be shorter
  // than one (see LENGTH_UNITS_PER_STEP - length is in those units, not
  // whole steps). midi is 'hit' for untunable instruments (see
  // utils/music-notes.js), otherwise the pitch's MIDI number; audf is that
  // specific note's  AUDF value (or null for a 'hit'), stored per-note so
  // playback doesn't have to re-derive it.
  notes: [],
});

// Two steps per beat (each step is an eighth note) - see
// utils/music-playback.js. 140 BPM matches FL Studio's  new-project
// default tempo.
export const DEFAULT_TEMPO = 140;

// Applies to every Tempo (BPM) field, song- and pattern-level alike (see
// MusicEditor.vue's  clampTempo, and the load-time clamp below for
// values from an older save/an imported file that predates this range).
export const MIN_TEMPO = 8;
export const MAX_TEMPO = 255;
export const clampTempo = (value) => {
  const tempo = Math.round(Number(value));
  return Number.isFinite(tempo) ? Math.min(MAX_TEMPO, Math.max(MIN_TEMPO, tempo)) : DEFAULT_TEMPO;
};

export const DEFAULT_SONGS = {
  songs: [
    {
      id: 1,
      name: 'Song 1',
      tempo: DEFAULT_TEMPO,
      // Whether the Music tab's  song-level preview playback (Play on
      // the song card, not any pattern's  preview - see
      // patternPreviewLoop below and handlePlaySong/playSequence) repeats
      // the whole sequence once it reaches the end, instead of stopping
      // there. A Music-tab-only preview convenience, like
      // patternPreviewLoop - has no bearing on the compiled ROM, which has
      // its  separate Loop checkbox on the "Play song" block itself (see
      // music_play_song in this same file).
      loop: false,
      // Same idea, for previewing a single PATTERN (see handleToggleLoopPattern
      // in MusicEditor.vue) - one shared preference for every pattern in
      // this song, not stored per pattern (a pattern object has no "loop"
      // field of its  at all anymore) - switching which pattern is
      // active used to silently carry over whatever THAT pattern's
      // stored flag happened to be, which read as "switching patterns turns
      // looping on" whenever the newly-selected one's  flag was true.
      patternPreviewLoop: false,
      patterns: [
        {
          id: 1,
          name: 'Pattern 1',
          tempo: DEFAULT_TEMPO,
          // Whether this pattern plays at its Tempo field (checked) or
          // follows its song's top-level one instead (unchecked) - a fresh
          // pattern follows the song by default, matching what most users
          // would expect.
          useOwnTempo: false,
          stepCount: DEFAULT_PATTERN_STEPS,
          tracks: [emptyTrack(1, 1)],
        },
      ],
      // {id, patternId, count} per Sequence entry - count > 1 is a pattern
      // repeated that many times in a row (see MusicEditor.vue's
      // handleSequenceResizeStart, which is how a user actually creates
      // one) - stored this way rather than one raw patternId per real
      // repeat specifically so a long repeated run doesn't bloat the
      // project JSON/localStorage with dozens of identical entries; see
      // normalizeSequenceGroups below for the migration from the old flat
      // shape. The compiled ROM keeps this same group shape too, rather
      // than expanding back to one entry per repeat - see
      // generators/bbasic/music.js's resolveProjectMusic (sequenceRepeatPacked)
      // and generateMusicChecks (the seqRepeatVar countdown) for the
      // runtime repeat counter that replays a group's  pattern in place
      // instead of needing a table row per real repeat.
      sequence: [{id: 1, patternId: 1, count: 1}],
    },
  ],
  // Optional per-sound-effect color overrides (soundEffectId -> TIA color
  // byte, matching utils/palette.js's index<<1 convention) - keyed globally
  // rather than per-song/pattern so the same instrument reads as the same
  // color everywhere it's used. Missing an entry means "auto-assigned"
  // (see instrumentColor in MusicEditor.vue).
  instrumentColors: {},
  // How finely a step is divided for note duration - global (not per-song),
  // set via the dropdown right under the Music tab's  header.
  subdivision: DEFAULT_SUBDIVISION,
  // A fresh project has no notes to migrate.
  noteLengthUnitsMigrated: true,
};

// Normalizes a song's  sequence into the current {id, patternId, count}
// shape - handles three cases at once: the OLD shape (a flat array of raw
// patternIds, one per real repeat, from a project saved before repeat
// groups existed - see DEFAULT_SONGS' comment), the current shape
// (defensively re-validated - a hand-edited or corrupted file could have a
// missing id/non-integer count), and an already-correct in-memory sequence
// (a no-op pass, safe to call unconditionally on every load rather than
// needing its  one-time migration flag the way the note-length rescale
// above does, since collapsing already-grouped entries can't lose
// information the way re-scaling an already-migrated note length would).
// Exported (not just used internally below) since MusicEditor.vue's
// handleImportSong needs this same normalization for an OLDER exported
// song .json file being imported into a project that's already on the new
// shape - that file's `sequence` bypasses this function entirely
// otherwise, since importing overwrites a song's fields directly rather
// than going through this storage-load path.
export const normalizeSequenceGroups = (sequence) => {
  const flat = Array.isArray(sequence) ? sequence : [];
  let nextId = 1;
  if (flat.length && flat[0] && typeof flat[0] === 'object') {
    return flat.map((group) => {
      const id = group.id != null ? group.id : nextId;
      nextId = Math.max(nextId, id + 1);
      return {id, patternId: group.patternId, count: Math.max(1, Math.round(Number(group.count) || 1))};
    }).filter((group) => group.patternId != null);
  }
  const groups = [];
  flat.forEach((patternId) => {
    const last = groups[groups.length - 1];
    if (last && last.patternId === patternId) {
      last.count++;
    } else {
      groups.push({id: nextId++, patternId, count: 1});
    }
  });
  return groups;
};

export const processSongsStorageDefaults = (songsStorage) => {
  const songs = songsStorage.value;
  if (!songs || !songs.songs || !songs.songs.length) {
    return structuredClone(DEFAULT_SONGS);
  }
  if (!songs.instrumentColors || typeof songs.instrumentColors !== 'object') {
    songs.instrumentColors = {};
  }
  if (!DURATION_SUBDIVISION_OPTIONS.includes(songs.subdivision)) {
    songs.subdivision = DEFAULT_SUBDIVISION;
  }
  // Notes saved before sub-step durations existed stored both their start
  // and length in whole steps directly, not LENGTH_UNITS_PER_STEP units - a
  // one-time, guarded rescale of both so those notes keep their original
  // position/duration instead of suddenly starting 32x earlier and playing
  // 32x shorter.
  if (!songs.noteLengthUnitsMigrated) {
    songs.songs.forEach((song) => {
      (song.patterns || []).forEach((pattern) => {
        (pattern.tracks || []).forEach((track) => {
          (track.notes || []).forEach((note) => {
            note.length = Math.max(1, Math.round(note.length || 1)) * LENGTH_UNITS_PER_STEP;
            note.step = Math.max(0, Math.round(note.step || 0)) * LENGTH_UNITS_PER_STEP;
          });
        });
      });
    });
    songs.noteLengthUnitsMigrated = true;
  }
  // Songs/patterns/tracks saved before the Tempo/Channel/step-count/
  // note-length fields existed won't have them yet.
  songs.songs.forEach((song) => {
    // Also re-clamps an already-set tempo, not just a missing one - an
    // older save (or an imported file - see MusicEditor.vue's
    // handleImportSong) can carry a value from before this range existed.
    song.tempo = clampTempo(song.tempo ?? DEFAULT_TEMPO);
    // A song saved before this field existed had no way to loop its
    // preview playback at all, so it always behaved like "off" - default it
    // to staying that way (see the field's  comment in DEFAULT_SONGS).
    if (typeof song.loop !== 'boolean') {
      song.loop = false;
    }
    // One shared preview-loop preference for every pattern in this song,
    // not stored per pattern (see handleToggleLoopPattern in
    // MusicEditor.vue) - switching which pattern you're looking at (e.g.
    // clicking a Sequence chip) used to silently carry over whatever THAT
    // pattern's  stored loop flag happened to be, which read as "clicking
    // a chip turns looping on" whenever the newly-selected pattern's
    // flag happened to be true, even though the user never touched the Loop
    // button at all. A single per-song preference means switching patterns
    // can never change it by itself. Not migrated FROM the old per-pattern
    // field (every pattern.loop that already exists is just left in place,
    // unused) - there's no single "right" value to carry forward when
    // different patterns in the same song may have disagreed, so this
    // starts fresh at the same "off" default a song saved before per-pattern
    // loop existed already got.
    if (typeof song.patternPreviewLoop !== 'boolean') {
      song.patternPreviewLoop = false;
    }
    song.sequence = normalizeSequenceGroups(song.sequence);
    (song.patterns || []).forEach((pattern) => {
      pattern.tempo = clampTempo(pattern.tempo ?? DEFAULT_TEMPO);
      // A pattern saved before this song-level Tempo field existed was
      // definitely relying on its  tempo, not a song-level one that
      // didn't exist yet - default it to staying that way, rather than
      // silently switching its playback speed to the (new, likely
      // different) song tempo.
      if (pattern.useOwnTempo == null) {
        pattern.useOwnTempo = true;
      }
      pattern.stepCount = clampPatternSteps(pattern.stepCount ?? DEFAULT_PATTERN_STEPS);
      (pattern.tracks || []).forEach((track) => {
        if (track.channel == null) {
          track.channel = 0;
        }
        // Pre-length-and-events tracks stored one slot per step directly
        // (null/AUDF/'hit') instead of a sparse {step, length} event list -
        // rather than replay that migration's edge cases, just drop the old
        // shape back to empty, since it was only ever this session's
        // test data.
        if (!Array.isArray(track.notes) || track.notes.some((note) => note === null || typeof note !== 'object')) {
          track.notes = [];
        }
      });
    });
  });
  return songs;
};

// Looks up one stored song by id, or null if it can't be found. Read fresh
// each time (not through the module-level storage) for the same reason as
// findDataTableById in blocks/data.js - a computed() over localStorage caches
// its first read and would keep serving a stale/renamed song list.
export const findSongById = (id) => {
  try {
    const songs = processSongsStorageDefaults(useSongsStorage());
    return songs.songs.find((song) => `${song.id}` === `${id}`) || null;
  } catch (e) {
    console.error('Failed to load song', e);
    return null;
  }
};

// Passing the function itself (not a static array) to FieldDropdown, like
// blocks/data.js's buildDataTableOptions, so Blockly rebuilds the list every
// time the dropdown opens - renamed/added/deleted songs show up without
// reloading the page.
const buildSongOptions = () => {
  try {
    const songs = processSongsStorageDefaults(useSongsStorage());
    return songs.songs.map(({id, name}) => [name || `Unnamed ${id}`, `${id}`]);
  } catch (e) {
    console.error('Failed to list song options', e);
    return [['Error', '1']];
  }
};

// Starts (or restarts) playback of one song created on the Music tab. Any
// number of distinct songs can be referenced across a project's
// music_play_song/music_play_song_by_id blocks (see resolveProjectMusic in
// generators/bbasic/music.js) - each included song gets its  reset
// subroutine once there's more than one to choose between.
Blockly.Blocks['music_play_song'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Play song`)
        .appendField(new Blockly.FieldDropdown(buildSongOptions), 'SONG')
        .appendField('Loop')
        .appendField(new Blockly.FieldCheckbox('TRUE'), 'LOOP');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Starts (or restarts) playback of a song created on the Music tab. ' +
      'When Loop is unchecked, the song plays once and stops instead of repeating.');
  },
};

// Same start/restart behavior as music_play_song above, but picks the song
// by a runtime VALUE (a variable or computed expression) instead of a fixed
// dropdown choice - since this can't be resolved at compile time, using this
// block anywhere in the project pulls EVERY song from storage into the
// compiled ROM, so whatever ID it's given at runtime always has real data to
// find (see resolveProjectMusic's  usesSongById comment). A separate
// block (not an added input on music_play_song) so existing projects using
// that one aren't disturbed.
Blockly.Blocks['music_play_song_by_id'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Play song`);
    this.appendValueInput('SONG_ID');
    this.appendDummyInput()
        .appendField('Loop')
        .appendField(new Blockly.FieldCheckbox('TRUE'), 'LOOP');
    this.setInputsInline(true);
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Same as "Play song", but picks the song by ID (a variable or computed value, ' +
      'not a fixed choice) - using this anywhere in the project makes every song available to pick ' +
      'from at runtime. When Loop is unchecked, the song plays once and stops instead of repeating.');
  },
};

// Reports whether the NAMED song specifically is the one currently playing
// (started by music_play_song/music_play_song_by_id, and not yet stopped or
// naturally finished) - true even while paused (see music_pause_song: a
// paused song is still "the one playing," just frozen, the same way this
// app's  musicPlayingBit already treats it). On a single-song project
// this is just "is anything playing at all" (see the generator's
// comment) - there's only ever one possible song to mean.
Blockly.Blocks['music_song_playing'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Song`)
        .appendField(new Blockly.FieldDropdown(buildSongOptions), 'SONG')
        .appendField('is playing');
    this.setOutput(true, 'Boolean');
    this.setColour(MUSIC_COLOR);
    this.setTooltip('True while the named song specifically is the one currently playing (including ' +
      'while paused) - false if it was never started, already stopped, or a DIFFERENT song is playing ' +
      'instead.');
  },
};

// Same check as music_song_playing above, but picks the song by a runtime
// VALUE (a variable or computed expression) instead of a fixed dropdown
// choice - same relationship music_song_stopped_by_number has to music_
// song_stopped_by_id. Using this anywhere in the project pulls EVERY song
// into the compiled ROM (see music_play_song_by_id's  comment) so
// whatever ID it's given at runtime always has real data to compare
// against.
Blockly.Blocks['music_song_playing_by_number'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Song ID`);
    this.appendValueInput('SONG_ID');
    this.appendDummyInput()
        .appendField('is playing');
    this.setInputsInline(true);
    this.setOutput(true, 'Boolean');
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Same as "Song ... is playing", but picks the song by ID (a variable or computed ' +
      'value, not a fixed choice) - using this anywhere in the project makes every song count toward ' +
      'this check.');
  },
};

// Immediately silences whatever song is currently playing (started by
// music_play_song) and marks it as stopped - a subsequent music_play_song
// restarts it from the beginning. Does not trigger music_song_stopped below
// (that only fires when a non-looping song reaches its  natural end).
Blockly.Blocks['music_stop_song'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} ${STOP_ICON} Stop music`);
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Immediately stops whatever song is currently playing. A subsequent "Play song" ' +
      'restarts it from the beginning - unlike Pause below, there\'s no way to resume from here.');
  },
};

// Freezes whichever notes are currently sounding on every channel (held at
// their current pitch/volume, not silenced) and stops advancing through the
// song entirely - no timers count down, no new notes are read - until a
// matching music_unpause_song block runs. Distinct from Stop above: Stop
// discards playback position (a subsequent Play always restarts from the
// beginning); Pause keeps it exactly where it was, ready to pick back up
// mid-song. Has no effect if nothing is currently playing, or if already
// paused.
Blockly.Blocks['music_pause_song'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Pause music`);
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Freezes music playback exactly where it is (the currently sounding notes hold, ' +
      'they don\'t go silent) until "Unpause music" runs. Unlike "Stop music", playback position isn\'t ' +
      'lost - resuming continues the song from where it was paused, not from the beginning.');
  },
};

// Resumes playback exactly where music_pause_song above left it - the same
// notes that were held pick back up counting down from wherever their
// timers were, not restarted. Has no effect if music isn't currently paused.
Blockly.Blocks['music_unpause_song'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Unpause music`);
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Resumes music playback frozen by "Pause music", continuing exactly where it left ' +
      'off. Has no effect if music isn\'t currently paused.');
  },
};

// Fires once, the moment a song played with Loop unchecked reaches its
// natural end - never fires for a looping song (which never reaches an
// "end"), and never fires from the Stop block above (that's an explicit user
// action, not the song finishing by itself).
Blockly.Blocks['music_song_stopped'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} ${STOP_ICON} When song has stopped playing`);
    this.appendStatementInput('DO');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Runs the connected blocks once, the moment a song played with Loop unchecked ' +
      'reaches its natural end. Never triggers for a looping song, or from the Stop block.');
  },
};

// Same underlying "just stopped" flag as music_song_stopped above (see
// generateMusicChecks in generators/bbasic/music.js), but filtered to only
// fire for THIS SPECIFIC song (see musicJustStoppedSongVarName's
// comment there, and music_song_stopped_by_id's generator) - the SONG
// dropdown used to be purely cosmetic, firing for whichever song happened
// to stop regardless of which one was actually named here, a real reported
// bug. On a single-song project there's nothing to distinguish it from
// music_song_stopped anyway, so it behaves identically there. A separate
// block (not an added field on music_song_stopped) so existing projects
// using that one aren't disturbed.
Blockly.Blocks['music_song_stopped_by_id'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} ${STOP_ICON} When song has stopped playing`)
        .appendField(new Blockly.FieldDropdown(buildSongOptions), 'SONG');
    this.appendStatementInput('DO');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Same as "When song has stopped playing", but only fires when the NAMED song ' +
      'specifically is the one that stopped, not any other song.');
  },
};

// Same filtered trigger as music_song_stopped_by_id above (see its
// comment), but picks the song by a runtime VALUE (a variable or computed
// ID) instead of a fixed dropdown choice - same reasoning as
// music_play_song_by_id vs music_play_song. A separate block (not an added
// input on music_song_stopped_by_id) so existing projects using that one
// aren't disturbed.
Blockly.Blocks['music_song_stopped_by_number'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} ${STOP_ICON} When song has stopped playing`);
    this.appendValueInput('SONG_ID');
    this.appendStatementInput('DO');
    this.setInputsInline(true);
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Same as "When song has stopped playing", but picks the song by ID (a variable or ' +
      'computed value, not a fixed choice), and only fires when that specific song is the one that ' +
      'stopped, not any other song.');
  },
};

// Fires once, the moment ANY Sequence chip (see the Sequence list on the
// Music tab) finishes - its  configured repeat count fully used up, not
// each individual repeat - and the song is about to move on to the next
// chip (or wrap/stop, for the last one). Deliberately doesn't distinguish
// WHICH chip, same "keep it simple" precedent music_song_stopped already
// sets for songs - see music_sequence_chip_finished_by_id below for that.
Blockly.Blocks['music_sequence_chip_finished'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} When any sequence chip has finished playing`);
    this.appendStatementInput('DO');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Runs the connected blocks once, the moment ANY Sequence chip (any song) finishes ' +
      'its configured repeat count and the song moves on. Doesn\'t distinguish which chip - see ' +
      '"When sequence chip [ID] has finished playing" for that.');
  },
};

// Same trigger as above, but for one specific chip - unlike
// music_song_stopped_by_id's SONG dropdown (cosmetic only, never actually
// checked), BOTH fields here are real: see generateSequenceChipFinished in
// generators/bbasic/music.js for how CHIP_ID gets resolved to this chip's
// compile-time sequence position and dispatched at runtime. CHIP_ID is
// a plain typed number (not a value input) - read it off the small "ID: N"
// badge next to each chip in the Sequence list, there's no existing need
// here for a computed/variable chip id the way music_play_song_by_id needs
// a computed song id.
//
// That badge (and this field) is the chip's  CURRENT POSITION in the
// Sequence list (1 = first), not a permanent identity - reordering,
// inserting, or deleting chips changes which chip a given number refers
// to. An earlier version of this used a separate, permanent id per chip
// instead (stable across reordering) - reverted at the user's  explicit
// request in favor of this simpler "number = position" model.
Blockly.Blocks['music_sequence_chip_finished_by_id'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} When sequence chip`)
        .appendField(new Blockly.FieldDropdown(buildSongOptions), 'SONG')
        .appendField('ID')
        .appendField(new Blockly.FieldNumber(1, 1), 'CHIP_ID')
        .appendField('has finished playing');
    this.appendStatementInput('DO');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Runs the connected blocks once, the moment the chosen song\'s Sequence chip in this ' +
      'position finishes its configured repeat count and the song moves on - read the position off ' +
      'the small "ID: N" badge next to that chip in the Sequence list (1 = first chip; changes if you ' +
      'reorder, insert, or delete chips before it). Does nothing if that song doesn\'t have that many chips.');
  },
};

// Same trigger as music_sequence_chip_finished_by_id above, but without its
// SONG dropdown - checks whichever song is CURRENTLY PLAYING (at the moment
// this chip actually finishes) for a Sequence chip in this position, instead
// of naming one fixed song up front. Different songs can each have a chip in
// the same position (position is only meaningful WITHIN one song's
// Sequence list, not project-wide), so this can genuinely fire from more
// than one song, just never more than one at a time (only one song is ever
// "currently playing"). See generateSequenceChipFinished in
// generators/bbasic/music.js for how this resolves EVERY song that has a
// chip in this position, each to its  compile-time sequence position,
// then dispatches at runtime on whichever one is actually active.
Blockly.Blocks['music_sequence_chip_finished_current_song'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} When sequence chip ID`)
        .appendField(new Blockly.FieldNumber(1, 1), 'CHIP_ID')
        .appendField('has finished playing (current song)');
    this.appendStatementInput('DO');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Runs the connected blocks once, the moment whichever song is CURRENTLY playing ' +
      'reaches a Sequence chip in this position and finishes its configured repeat count - checks ' +
      'every song that has a chip in this position, not just one fixed song. Read the position off the ' +
      'small "ID: N" badge next to that chip in the Sequence list (1 = first chip; changes if you reorder, ' +
      'insert, or delete chips before it). Does nothing if no song has that many chips.');
  },
};

// Live "is it happening right now" check, unlike music_sequence_chip_
// finished_by_id's one-shot watch above - true for the entire time the
// chosen song is the one currently playing AND its  Sequence position
// is at this chip, not just once at the moment it's reached. Mirrors
// music_song_playing's "still counts as playing while paused"
// convention. See music_sequence_chip_playing_current_song below for the
// "whichever song is playing" version, and generators/bbasic/music.js for
// how CHIP_ID/SONG resolve to a compile-time sequence position (same
// primaryChannelFor/chipIdToSeqIndex resolveMusicEventFlags already uses
// for the "finished" watch, now shared rather than needing a flag bit
// - a live check has nothing to watch-and-clear).
Blockly.Blocks['music_sequence_chip_playing_by_id'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Sequence chip`)
        .appendField(new Blockly.FieldDropdown(buildSongOptions), 'SONG')
        .appendField('ID')
        .appendField(new Blockly.FieldNumber(1, 1), 'CHIP_ID')
        .appendField('is playing');
    this.setOutput(true, 'Boolean');
    this.setColour(MUSIC_COLOR);
    this.setTooltip('True for as long as the chosen song is the one currently playing (including while ' +
      'paused) AND its Sequence position is at this chip - read the position off the small "ID: N" ' +
      'badge next to that chip in the Sequence list (1 = first chip; changes if you reorder, insert, or ' +
      'delete chips before it). False if that song doesn\'t have that many chips.');
  },
};

// Same live check as music_sequence_chip_playing_by_id above, but without
// its SONG dropdown - checks whichever song is CURRENTLY PLAYING, the same
// relationship music_sequence_chip_finished_current_song already has to
// music_sequence_chip_finished_by_id.
Blockly.Blocks['music_sequence_chip_playing_current_song'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} Sequence chip ID`)
        .appendField(new Blockly.FieldNumber(1, 1), 'CHIP_ID')
        .appendField('is playing (current song)');
    this.setOutput(true, 'Boolean');
    this.setColour(MUSIC_COLOR);
    this.setTooltip('True for as long as whichever song is CURRENTLY playing has its Sequence ' +
      'position at a chip in this position - checks every song that has a chip in this position, not just ' +
      'one fixed song. Read the position off the small "ID: N" badge next to that chip in the Sequence ' +
      'list (1 = first chip; changes if you reorder, insert, or delete chips before it). False if no song ' +
      'has that many chips, or nothing is playing.');
  },
};

// Fires once, the moment ANY channel starts a new note using this
// instrument - not just the first note, every single one, for as long as
// the song keeps playing. "Instrument" here means the Sound tab preset a
// track is pointed at when its  notes get compiled in, not any one
// specific note/track/channel - the same instrument used on more than one
// track (even across different songs) fires this from any of them. See
// resolveNotePlayedInstruments in generators/bbasic/music.js for how each
// watched instrument gets its  small packed ID baked into the compiled
// note data (there's no room to store a whole instrument reference per
// note, only a few spare bits), and generateMusicChecks for where that ID
// is compared against, right as each new note is actually fetched.
Blockly.Blocks['music_note_played'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} When a note is played by instrument`)
        .appendField(new Blockly.FieldDropdown(buildSoundEffectOptions), 'INSTRUMENT');
    this.appendStatementInput('DO');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Runs the connected blocks once every time any channel starts a new note using ' +
      'this instrument (a Sound tab preset), on any track or song that uses it. Up to 14 distinct ' +
      'instruments can be watched this way project-wide (counting both this block and "When a note is ' +
      'played by instrument ID").');
  },
};

// Same trigger as music_note_played above, just chosen by typing its  ID
// number directly (see the ID badge on its card on the Sound tab) instead
// of picking it from a name dropdown - same relationship
// data_get_element_by_id has to data_get_element.
Blockly.Blocks['music_note_played_by_id'] = {
  init: function() {
    this.appendDummyInput()
        .appendField(`${MUSIC_ICON} When a note is played by instrument ID`)
        .appendField(new Blockly.FieldNumber(1, 1), 'INSTRUMENT_ID');
    this.appendStatementInput('DO');
    this.setPreviousStatement(true);
    this.setNextStatement(true);
    this.setColour(MUSIC_COLOR);
    this.setTooltip('Same as "When a note is played by instrument", just chosen by typing its ID ' +
      'number (see the ID badge on its card on the Sound tab) instead of picking it from a dropdown.');
  },
};

